import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Pagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import {
  Refresh,
  Visibility,
  Sync
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { TenantDeployment, DeploymentStatus, DeploymentOperation } from '../types';
import StatusChip from '../components/StatusChip';

const DeploymentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deployments, setDeployments] = useState<TenantDeployment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<DeploymentStatus | ''>('');
  const [operationFilter, setOperationFilter] = useState<DeploymentOperation | ''>('');

  // Logs dialog
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedDeploymentLogs, setSelectedDeploymentLogs] = useState<string>('');
  const [selectedDeployment, setSelectedDeployment] = useState<TenantDeployment | null>(null);

  const loadDeployments = async () => {
    try {
      setError('');
      const response = await apiService.getAllDeployments({
        page,
        per_page: 20,
        status: statusFilter || undefined,
        operation: operationFilter || undefined
      });
      
      setDeployments(response.deployments);
      setTotalPages(response.pages);
    } catch (err: any) {
      console.error('Failed to load deployments:', err);
      setError(err.detail || 'Failed to load deployments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewLogs = async (deployment: TenantDeployment) => {
    try {
      // Clear previous logs to avoid showing stale data
      setSelectedDeploymentLogs('Loading logs...');
      setSelectedDeployment(deployment);
      setLogsDialogOpen(true);
      
      // Fetch fresh logs every time
      const logsData = await apiService.getDeploymentLogs(deployment.id);
      setSelectedDeploymentLogs(logsData.logs);
    } catch (err: any) {
      console.error('Failed to load logs:', err);
      setSelectedDeploymentLogs('Failed to load logs: ' + (err.detail || err.message || 'Unknown error'));
      toast.error(err.detail || 'Failed to load deployment logs');
    }
  };

  const handleRefreshLogs = async () => {
    if (selectedDeployment) {
      try {
        setSelectedDeploymentLogs('Refreshing logs...');
        const logsData = await apiService.getDeploymentLogs(selectedDeployment.id);
        setSelectedDeploymentLogs(logsData.logs);
        toast.success('Logs refreshed');
      } catch (err: any) {
        console.error('Failed to refresh logs:', err);
        setSelectedDeploymentLogs('Failed to refresh logs: ' + (err.detail || err.message || 'Unknown error'));
        toast.error(err.detail || 'Failed to refresh logs');
      }
    }
  };

  const handleSyncDeployment = async (deployment: TenantDeployment) => {
    try {
      await apiService.syncDeployment(deployment.id);
      toast.success('Deployment status synchronized');
      loadDeployments();
    } catch (err: any) {
      console.error('Failed to sync deployment:', err);
      toast.error(err.detail || 'Failed to sync deployment status');
    }
  };

  useEffect(() => {
    loadDeployments();
  }, [page, statusFilter, operationFilter]);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('deployment.deployments')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => loadDeployments()}
        >
          {t('common.refresh')}
        </Button>
      </Box>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('deployment.status', { defaultValue: 'Status' })}</InputLabel>
          <Select
            value={statusFilter}
            label={t('deployment.status', { defaultValue: 'Status' })}
            onChange={(e) => setStatusFilter(e.target.value as DeploymentStatus | '')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="pending">{t('status.pending')}</MenuItem>
            <MenuItem value="in_progress">{t('status.in_progress')}</MenuItem>
            <MenuItem value="completed">{t('status.completed')}</MenuItem>
            <MenuItem value="failed">{t('status.failed')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('deployment.operation')}</InputLabel>
          <Select
            value={operationFilter}
            label={t('deployment.operation')}
            onChange={(e) => setOperationFilter(e.target.value as DeploymentOperation | '')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="create">{t('operations.create')}</MenuItem>
            <MenuItem value="delete">{t('operations.delete')}</MenuItem>
            <MenuItem value="update">{t('operations.update')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Deployments Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              {t('deployment.loadingDeployments')}
            </Box>
          ) : deployments.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography color="text.secondary">
                {t('deployment.noDeployments')}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('deployment.tenantId')}</TableCell>
                    <TableCell>{t('deployment.operation')}</TableCell>
                    <TableCell>{t('dashboard.status')}</TableCell>
                    <TableCell>{t('deployment.jobName')}</TableCell>
                    <TableCell>{t('deployment.started')}</TableCell>
                    <TableCell>{t('deployment.completed')}</TableCell>
                    <TableCell>{t('deployment.duration')}</TableCell>
                    <TableCell>{t('tenant.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deployments.map((deployment) => (
                    <TableRow key={deployment.id} hover>
                      <TableCell>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate(`/tenants/${deployment.tenant_id}`)}
                        >
                          {deployment.tenant_id}
                        </Button>
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          label={deployment.operation_type} 
                          size="small"
                          variant="outlined"
                          color={deployment.operation_type === 'create' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <StatusChip status={deployment.status} />
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {deployment.job_name || '-'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(deployment.started_at), 'MMM dd, HH:mm:ss')}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {deployment.completed_at 
                            ? format(new Date(deployment.completed_at), 'MMM dd, HH:mm:ss')
                            : '-'
                          }
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {deployment.duration_seconds 
                            ? `${Math.round(deployment.duration_seconds / 60)}m ${deployment.duration_seconds % 60}s`
                            : '-'
                          }
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewLogs(deployment)}
                            title={t('common.viewLogs')}
                          >
                            <Visibility />
                          </IconButton>
                          
                          {deployment.status === 'in_progress' && (
                            <IconButton
                              size="small"
                              onClick={() => handleSyncDeployment(deployment)}
                              title={t('common.syncStatus')}
                            >
                              <Sync />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Logs Dialog */}
      <Dialog open={logsDialogOpen} onClose={() => setLogsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Deployment Logs
          {selectedDeployment && (
            <Typography variant="subtitle1" color="text.secondary">
              {selectedDeployment.operation_type} operation for tenant {selectedDeployment.tenant_id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              overflow: 'auto',
              maxHeight: '60vh',
              whiteSpace: 'pre-wrap',
              border: '1px solid #e0e0e0'
            }}
          >
            {selectedDeploymentLogs || 'No logs available'}
          </Box>
          
          {selectedDeployment?.error_message && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Error Message:
              </Typography>
              <Typography variant="body2" component="pre">
                {selectedDeployment.error_message}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefreshLogs}
            disabled={!selectedDeployment}
          >
            Refresh Logs
          </Button>
          {selectedDeployment && (
            <Button
              variant="outlined"
              onClick={() => {
                handleSyncDeployment(selectedDeployment);
                setLogsDialogOpen(false);
              }}
            >
              Sync Status
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeploymentsPage;